import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import FacultyLayout from './components/faculty/FacultyLayout';
import Button from './components/faculty/Button';
import Loader from './components/faculty/Loader';
import Toast from './components/faculty/Toast';
import './EditCourse.css';

const API_BASE = '/api';
const WIZARD_STEPS = ['Basic Info', 'Syllabus', 'Structure', 'Content'];

const EMPTY_BASIC = {
  course_name: '',
  description: '',
  prg_id: '',
  acd_id: '',
  duration_weeks: 1,
  difficulty_level: 'beginner',
  status: 'active'
};

const newLocalId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function EditCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const fetchedRef = useRef(false);
  const dirtyRef = useRef(false);

  const [facultyName, setFacultyName] = useState('');
  const [facultyId, setFacultyId] = useState('');
  const [userId, setUserId] = useState('');
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'success' });

  const [basicInfo, setBasicInfo] = useState(EMPTY_BASIC);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [units, setUnits] = useState([]);
  const [structureWeeks, setStructureWeeks] = useState([]);
  const [contents, setContents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [tests, setTests] = useState([]);

  const draftKey = useMemo(() => `edit_course_draft_${courseId}`, [courseId]);

  const markDirty = () => {
    dirtyRef.current = true;
  };

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    if (!dirtyRef.current) return;
    const draft = {
      basicInfo,
      units,
      structureWeeks,
      contents,
      assignments,
      tests,
      step
    };
    localStorage.setItem(draftKey, JSON.stringify(draft));
  }, [basicInfo, units, structureWeeks, contents, assignments, tests, step, draftKey]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const bootstrap = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user?.user_id) {
          setToast({ text: 'Session expired. Please login again.', type: 'error' });
          navigate('/', { replace: true });
          return;
        }
        setUserId(user.user_id);

        const [profileRes, programsRes, yearsRes] = await Promise.all([
          axios.get(`${API_BASE}/faculty/by-user/${encodeURIComponent(user.user_id)}`),
          axios.get(`${API_BASE}/programs`),
          axios.get(`${API_BASE}/academic-years`)
        ]);
        const fac = profileRes.data || {};
        setFacultyName(fac.fac_name || '');
        setFacultyId(fac.fac_id || '');
        setPrograms(programsRes.data || []);
        setAcademicYears(yearsRes.data || []);

        const savedDraftRaw = localStorage.getItem(draftKey);
        let draftData = null;
        if (savedDraftRaw) {
          try {
            draftData = JSON.parse(savedDraftRaw);
          } catch {
            localStorage.removeItem(draftKey);
          }
        }

        const [courseRes, structureRes, contentsRes, assignmentsRes, testsRes, unitsRes] = await Promise.all([
          axios.get(`${API_BASE}/courses/${encodeURIComponent(courseId)}`, {
            params: { faculty_id: fac.fac_id, user_id: user.user_id }
          }),
          axios.get(`${API_BASE}/courses/${encodeURIComponent(courseId)}/structure`, {
            params: { faculty_id: fac.fac_id, user_id: user.user_id }
          }).catch(() => ({ data: { course: {}, units: [], weeks: [] } })),
          axios.get(`${API_BASE}/contents/by-course/${encodeURIComponent(courseId)}`).catch(() => ({ data: [] })),
          axios.get(`${API_BASE}/assignments/by-course/${encodeURIComponent(courseId)}`).catch(() => ({ data: [] })),
          axios.get(`${API_BASE}/tests/by-course/${encodeURIComponent(courseId)}/full`).catch(() => ({ data: [] })),
          axios.get(`${API_BASE}/syllabus-units/${encodeURIComponent(courseId)}`).catch(() => ({ data: [] }))
        ]);

        const course = courseRes.data || {};
        
        // Use draft data for basic_info if available, otherwise use course data
        const loadedBasic = draftData?.basicInfo || {
          course_name: course.course_name || '',
          description: course.description || '',
          prg_id: course.prg_id || '',
          acd_id: course.acd_id || '',
          duration_weeks: Number(course.duration_weeks || 1),
          difficulty_level: course.difficulty_level || 'beginner',
          status: course.status || 'active'
        };
        setBasicInfo(loadedBasic);

        // Use draft units if available, otherwise load from API
        const loadedUnits = draftData?.units || (unitsRes.data || []).map((u) => ({
          unit_id: u.unit_id,
          title: u.title || '',
          description: u.description || '',
          unit_order: u.unit_order || 0
        }));
        setUnits(loadedUnits);

        // Use draft weeks if available, otherwise load from API
        const loadedWeeks = draftData?.structureWeeks || (structureRes.data?.weeks || []).map((w) => ({
          week_no: Number(w.week_no),
          title: w.title || `Week ${w.week_no}`,
          description: w.description || '',
          units: (w.units || []).map((u) => ({
            syllabus_id: u.syllabus_id || null,
            unit_title: u.unit_title || '',
            unit_content: u.unit_content || ''
          }))
        }));
        setStructureWeeks(loadedWeeks.length ? loadedWeeks : [{ week_no: 1, title: 'Week 1', description: '', units: [] }]);

        // ALWAYS load fresh content/assignments/tests from API (don't use draft for these)
        // This ensures Step 4 always has access to the latest uploaded content
        setContents(
          (contentsRes.data || []).map((c) => ({
            local_id: newLocalId('cnt'),
            content_id: c.content_id,
            title: c.title || '',
            week_no: Number(c.week_no || 1),
            content_type: c.content_type || 'note',
            existing_file: c.file_path || '',
            file: null,
            delete: false
          }))
        );

        setAssignments(
          (assignmentsRes.data || []).map((a) => ({
            local_id: newLocalId('asn'),
            assignment_id: a.assignment_id,
            title: a.title || '',
            description: a.description || '',
            week_no: Number(a.week_no || 1),
            due_date: a.due_date ? a.due_date.slice(0, 10) : '',
            existing_file: a.file_path || '',
            file: null,
            delete: false
          }))
        );

        setTests(
          (testsRes.data || []).map((t) => ({
            local_id: newLocalId('tst'),
            test_id: t.test_id,
            title: t.title || '',
            week_no: Number(t.week_no || 1),
            type: t.type || 'mcq',
            duration_minutes: Number(t.duration_minutes || 30),
            questions: Array.isArray(t.questions) ? t.questions : [],
            delete: false
          }))
        );

        // Use draft step if available and it's <= 3 (allow resuming edits)
        // But always load fresh content, so set step to 1 if draft is missing or invalid
        if (draftData && draftData.step && draftData.step >= 1 && draftData.step <= 3) {
          setStep(draftData.step);
          dirtyRef.current = true;
          setToast({ text: 'Loaded unsaved draft.', type: 'success' });
        } else {
          setStep(1);
          dirtyRef.current = false;
        }

        dirtyRef.current = false;
      } catch (err) {
        setToast({ text: err.response?.data?.error || 'Failed to load edit wizard.', type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [courseId, draftKey, navigate]);

  const progressPercent = Math.round((step / WIZARD_STEPS.length) * 100);

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!basicInfo.course_name || basicInfo.course_name.trim().length < 5) return 'Title must be at least 5 characters.';
      if (!basicInfo.description || basicInfo.description.trim().length < 20) return 'Description must be at least 20 characters.';
      if (!basicInfo.prg_id) return 'Program is required.';
      if (!basicInfo.acd_id) return 'Academic year is required.';
      if (!Number.isFinite(Number(basicInfo.duration_weeks)) || Number(basicInfo.duration_weeks) <= 0) return 'Total weeks must be greater than 0.';
    }
    return '';
  };

  const saveBasicInfoAndGoNext = async () => {
    const err = validateStep(1);
    if (err) {
      setToast({ text: err, type: 'error' });
      return;
    }

    try {
      setSaving(true);

      // Use PATCH to only update basic info — don't touch content/structure/assignments/tests
      const response = await axios.patch(`${API_BASE}/courses/${encodeURIComponent(courseId)}`, {
        faculty_id: facultyId,
        user_id: userId,
        course_name: basicInfo.course_name,
        description: basicInfo.description,
        prg_id: basicInfo.prg_id,
        acd_id: basicInfo.acd_id,
        duration_weeks: Number(basicInfo.duration_weeks || 1),
        difficulty_level: basicInfo.difficulty_level,
        status: basicInfo.status
      });

      if (response.data.success) {
        dirtyRef.current = false;
        setToast({ text: 'Basic info saved. Moving to next step...', type: 'success' });
        // Move to Step 2 after successful save
        setTimeout(() => {
          setStep(2);
        }, 300);
      } else {
        setToast({ text: response.data.error || 'Failed to save basic info.', type: 'error' });
      }
    } catch (e) {
      setToast({ text: e.response?.data?.error || 'Failed to save basic info.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    console.log(`[Navigation] goNext called, current step: ${step}`);
    // Save before moving forward on Step 1, Step 2, and Step 3
    if (step === 1) {
      console.log('[Navigation] Saving basic info...');
      await saveBasicInfoAndGoNext();
    } else if (step === 2) {
      console.log('[Navigation] Saving units...');
      await saveUnitsAndGoNext();
    } else if (step === 3) {
      console.log('[Navigation] Saving structure...');
      await saveStructureAndGoNext();
    } else {
      // For other steps, just navigate forward
      console.log('[Navigation] Just advancing step');
      setStep((s) => Math.min(s + 1, 4));
    }
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const cancelEdit = () => {
    if (dirtyRef.current && !window.confirm('You have unsaved changes. Leave this page?')) return;
    localStorage.removeItem(draftKey);
    navigate('/faculty-dashboard/my-courses');
  };

  // ===== Unit Management — Step 2 (Setup → Detail modes) =====
  const [unitCount, setUnitCount] = useState('');           // numeric input value
  const [unitsGenerated, setUnitsGenerated] = useState(false); // true once cards rendered

  // Detect if units already exist (edit-mode re-entry)
  useEffect(() => {
    if (units.length > 0 && !unitsGenerated) {
      setUnitCount(String(units.length));
      setUnitsGenerated(true);
    }
  }, [units, unitsGenerated]);

  const generateUnits = () => {
    const count = parseInt(unitCount, 10);
    if (!count || count < 1 || count > 30) {
      setToast({ text: 'Enter a number between 1 and 30.', type: 'error' });
      return;
    }
    markDirty();
    // Preserve existing units that fit within the new count; pad or trim
    setUnits((prev) => {
      const next = [];
      for (let i = 0; i < count; i++) {
        if (prev[i]) {
          next.push({ ...prev[i], unit_order: i + 1 });
        } else {
          next.push({ unit_id: null, title: '', description: '', unit_order: i + 1 });
        }
      }
      return next;
    });
    setUnitsGenerated(true);
  };

  const resetUnitCount = async () => {
    if (!window.confirm('This will clear all unit data. Continue?')) return;
    // Delete server-side units first
    for (const u of units) {
      if (u.unit_id) {
        try { await axios.delete(`${API_BASE}/syllabus-units/${u.unit_id}`); } catch (_) { /* ignore */ }
      }
    }
    setUnits([]);
    setUnitCount('');
    setUnitsGenerated(false);
    markDirty();
  };

  const updateUnit = (index, field, value) => {
    markDirty();
    setUnits((prev) => prev.map((u, i) => (i === index ? { ...u, [field]: value } : u)));
  };

  // Are all generated unit titles filled?
  const allUnitTitlesFilled = units.length > 0 && units.every((u) => u.title && u.title.trim().length > 0);

  const saveUnitsAndGoNext = async () => {
    // Validate at least one unit with title
    const validUnits = units.filter((u) => u.title && u.title.trim().length > 0);
    
    if (validUnits.length === 0) {
      setToast({ text: 'At least one unit with a title is required', type: 'error' });
      return;
    }

    try {
      setSaving(true);

      // Prepare units for saving (add order if not set)
      const unitsToSave = validUnits.map((u, idx) => ({
        unit_id: u.unit_id || null,
        title: u.title.trim(),
        description: (u.description || '').trim(),
        unit_order: u.unit_order || idx + 1
      }));

      // Create/update units using batch operation
      // First, fetch existing units from server
      const existingUnitsRes = await axios.get(`${API_BASE}/syllabus-units/${encodeURIComponent(courseId)}`);
      const existingUnitsMap = {};
      (existingUnitsRes.data || []).forEach(u => {
        existingUnitsMap[u.unit_id] = u;
      });

      // Process each unit (create new or update existing)
      const responses = [];
      for (const unit of unitsToSave) {
        if (unit.unit_id && existingUnitsMap[unit.unit_id]) {
          // Update existing unit
          const updateRes = await axios.put(`${API_BASE}/syllabus-units/${unit.unit_id}`, {
            title: unit.title,
            description: unit.description,
            unit_order: unit.unit_order
          });
          responses.push(updateRes.data);
        } else {
          // Create new unit
          const createRes = await axios.post(`${API_BASE}/syllabus-units`, {
            course_id: courseId,
            title: unit.title,
            description: unit.description,
            unit_order: unit.unit_order
          });
          responses.push(createRes.data);
        }
      }

      // Fetch all units again to get fresh IDs
      const response = await axios.get(`${API_BASE}/syllabus-units/${encodeURIComponent(courseId)}`);

      if (response.data && Array.isArray(response.data)) {
        // Update units with server-generated IDs
        const savedUnits = response.data || [];
        setUnits(savedUnits.map((u) => ({
          unit_id: u.unit_id,
          title: u.title,
          description: u.description || '',
          unit_order: u.unit_order
        })));
        
        dirtyRef.current = false;
        setToast({ text: 'Units saved. Moving to Structure...', type: 'success' });
        
        setTimeout(() => {
          setStep(3);
        }, 300);
      } else {
        setToast({ text: response.data.error || 'Failed to save units', type: 'error' });
      }
    } catch (e) {
      setToast({ text: e.response?.data?.error || 'Failed to save units', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const saveStructureAndGoNext = async () => {
    try {
      setSaving(true);
      const MAX_RETRIES = 3;
      console.log('[Step3] Saving structure for', structureWeeks.length, 'weeks');

      for (let i = 0; i < structureWeeks.length; i++) {
        const w = structureWeeks[i];
        let lastError = null;
        console.log(`[Step3] Processing Week ${w.week_no}:`, { week_id: w.week_id, title: w.title });

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            if (w.week_id) {
              // Update existing week
              console.log(`[Step3] Attempt ${attempt}: PUT ${w.week_id}`);
              await axios.put(`${API_BASE}/weekly-plan/${w.week_id}`, {
                title: w.title,
                description: w.description || '',
                unit_ids: w.units || [],
                week_number: w.week_no
              });
              console.log(`[Step3] PUT succeeded for ${w.week_id}`);
            } else {
              // Create new week
              console.log(`[Step3] Attempt ${attempt}: POST new week`);
              const res = await axios.post(`${API_BASE}/weekly-plan`, {
                course_id: courseId,
                week_number: w.week_no,
                title: w.title,
                description: w.description || '',
                unit_ids: w.units || [],
                week_order: w.week_no
              });
              structureWeeks[i] = { ...w, week_id: res.data.week_id };
              console.log(`[Step3] POST created week with ID: ${res.data.week_id}`);
            }
            lastError = null;
            break; // Success, exit retry loop
          } catch (error) {
            lastError = error;
            console.error(`[Step3] Attempt ${attempt} failed:`, error.response?.data || error.message);
            if (attempt < MAX_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }

        if (lastError) {
          const msg = `Failed to save Week ${w.week_no} after ${MAX_RETRIES} attempts: ${lastError.message}`;
          console.error('[Step3]', msg);
          throw new Error(msg);
        }
      }

      console.log('[Step3] All weeks saved successfully, moving to Step 4');
      setStructureWeeks([...structureWeeks]);
      dirtyRef.current = false;
      setToast({ text: 'Structure saved. Moving to Content...', type: 'success' });
      
      setTimeout(() => {
        console.log('[Step3] Navigating to Step 4');
        setStep(4);
      }, 300);
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message || 'Failed to save structure';
      console.error('[Step3] Save failed:', errMsg);
      setToast({ text: errMsg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ===== Week Management Functions (Step 3) =====
  const addWeek = () => {
    markDirty();
    setStructureWeeks((prev) => {
      const nextWeekNo = prev.length ? Math.max(...prev.map((w) => w.week_no)) + 1 : 1;
      const next = [...prev, { week_no: nextWeekNo, title: `Week ${nextWeekNo}`, description: '', units: [] }];
      setBasicInfo((b) => ({ ...b, duration_weeks: next.length }));
      return next;
    });
  };

  const removeWeek = (weekNo) => {
    markDirty();
    setStructureWeeks((prev) => {
      if (prev.length <= 1) return prev;
      const filtered = prev.filter((w) => w.week_no !== weekNo).map((w, idx) => ({ ...w, week_no: idx + 1 }));
      setBasicInfo((b) => ({ ...b, duration_weeks: filtered.length }));
      return filtered;
    });
    setContents((prev) => prev.filter((c) => c.week_no !== weekNo));
    setAssignments((prev) => prev.filter((a) => a.week_no !== weekNo));
    setTests((prev) => prev.filter((t) => t.week_no !== weekNo));
  };

  const updateWeek = (weekNo, key, value) => {
    markDirty();
    setStructureWeeks((prev) => prev.map((w) => (w.week_no === weekNo ? { ...w, [key]: value } : w)));
  };

  const upsertContent = (localId, key, value) => {
    markDirty();
    setContents((prev) => prev.map((c) => (c.local_id === localId ? { ...c, [key]: value } : c)));
  };
  const addContent = (weekNo) => {
    markDirty();
    setContents((prev) => [
      ...prev,
      { local_id: newLocalId('cnt'), content_id: null, title: '', week_no: weekNo, content_type: 'note', existing_file: '', file: null, delete: false }
    ]);
  };
  const deleteContent = (localId) => {
    markDirty();
    setContents((prev) =>
      prev
        .map((c) => (c.local_id === localId ? { ...c, delete: true } : c))
        .filter((c) => !(c.local_id === localId && !c.content_id))
    );
  };

  const upsertAssignment = (localId, key, value) => {
    markDirty();
    setAssignments((prev) => prev.map((a) => (a.local_id === localId ? { ...a, [key]: value } : a)));
  };
  const addAssignment = (weekNo) => {
    markDirty();
    setAssignments((prev) => [
      ...prev,
      {
        local_id: newLocalId('asn'),
        assignment_id: null,
        title: '',
        description: '',
        week_no: weekNo,
        due_date: '',
        existing_file: '',
        file: null,
        delete: false
      }
    ]);
  };
  const deleteAssignment = (localId) => {
    markDirty();
    setAssignments((prev) =>
      prev
        .map((a) => (a.local_id === localId ? { ...a, delete: true } : a))
        .filter((a) => !(a.local_id === localId && !a.assignment_id))
    );
  };

  const upsertTest = (localId, key, value) => {
    markDirty();
    setTests((prev) => prev.map((t) => (t.local_id === localId ? { ...t, [key]: value } : t)));
  };
  const addTest = (weekNo) => {
    markDirty();
    setTests((prev) => [
      ...prev,
      {
        local_id: newLocalId('tst'),
        test_id: null,
        title: '',
        week_no: weekNo,
        type: 'mcq',
        duration_minutes: 30,
        questions: [{ question: '', options: ['', '', '', ''], answer: '', marks: 1 }],
        delete: false
      }
    ]);
  };
  const deleteTest = (localId) => {
    markDirty();
    setTests((prev) =>
      prev
        .map((t) => (t.local_id === localId ? { ...t, delete: true } : t))
        .filter((t) => !(t.local_id === localId && !t.test_id))
    );
  };
  const addQuestion = (localId) => {
    markDirty();
    setTests((prev) =>
      prev.map((t) =>
        t.local_id === localId
          ? { ...t, questions: [...(t.questions || []), { question: '', options: ['', '', '', ''], answer: '', marks: 1 }] }
          : t
      )
    );
  };
  const updateQuestion = (localId, qIdx, key, value) => {
    markDirty();
    setTests((prev) =>
      prev.map((t) =>
        t.local_id === localId
          ? {
              ...t,
              questions: (t.questions || []).map((q, i) => (i === qIdx ? { ...q, [key]: value } : q))
            }
          : t
      )
    );
  };

  const handleFinalUpdate = async () => {
    const err = validateStep(1);
    if (err) {
      setToast({ text: err, type: 'error' });
      setStep(1);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        faculty_id: facultyId,
        user_id: userId,
        basic_info: {
          ...basicInfo,
          duration_weeks: Number(basicInfo.duration_weeks || 1)
        },
        structure: {
          weeks: structureWeeks.map((w) => ({
            week_no: Number(w.week_no),
            title: w.title || `Week ${w.week_no}`,
            description: w.description || '',
            units: (w.units || []).map((u) => ({
              syllabus_id: u.syllabus_id || null,
              unit_title: u.unit_title || '',
              unit_content: u.unit_content || ''
            }))
          }))
        },
        contents: { items: [] },
        assignments: { items: [] },
        tests: {
          items: tests.map((t) => ({
            test_id: t.test_id || undefined,
            week_no: Number(t.week_no || 1),
            title: t.title || '',
            type: t.type || 'mcq',
            duration_minutes: Number(t.duration_minutes || 30),
            questions: t.questions || [],
            delete: !!t.delete
          }))
        }
      };

      const formData = new FormData();
      if (thumbnailFile) {
        // retained in payload contract, backend currently ignores thumbnail safely.
        const tKey = `thumbnail_${Date.now()}`;
        formData.append(tKey, thumbnailFile);
        payload.basic_info.thumbnail_upload_key = tKey;
      }

      contents.forEach((c) => {
        const item = {
          content_id: c.content_id || undefined,
          week_no: Number(c.week_no || 1),
          title: c.title || '',
          content_type: c.content_type || 'note',
          file_path: c.existing_file || '',
          delete: !!c.delete
        };
        if (c.file) {
          const key = `content_${c.local_id}`;
          formData.append(key, c.file);
          item.upload_key = key;
        }
        payload.contents.items.push(item);
      });

      assignments.forEach((a) => {
        const item = {
          assignment_id: a.assignment_id || undefined,
          week_no: Number(a.week_no || 1),
          title: a.title || '',
          description: a.description || '',
          due_date: a.due_date || '',
          file_path: a.existing_file || '',
          delete: !!a.delete
        };
        if (a.file) {
          const key = `assignment_${a.local_id}`;
          formData.append(key, a.file);
          item.upload_key = key;
        }
        payload.assignments.items.push(item);
      });

      formData.append('payload', JSON.stringify(payload));
      await axios.put(`${API_BASE}/courses/${encodeURIComponent(courseId)}/full-update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      dirtyRef.current = false;
      localStorage.removeItem(draftKey);
      setToast({ text: 'Course updated successfully.', type: 'success' });
      setTimeout(() => navigate('/faculty-dashboard/my-courses'), 600);
    } catch (e) {
      setToast({ text: e.response?.data?.error || 'Failed to update course.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FacultyLayout title="Edit Course Wizard">
        <div className="edit-course-fullscreen">
          <div className="edit-course-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Loader label="Loading course editor..." />
          </div>
        </div>
      </FacultyLayout>
    );
  }

  return (
    <FacultyLayout title="Edit Course Wizard">
      <div className="edit-course-fullscreen">
      <div className="edit-course-header">
        <div className="edit-course-header-left">
          <h1>Edit Course Wizard</h1>
          <p>Update full course details across all steps</p>
        </div>
        <div className="edit-course-header-right">
          Step {step} of 4
        </div>
      </div>
      <Toast text={toast.text} type={toast.type} />

      {loading ? <Loader label="Loading course..." /> : (
        <div className="edit-course-container">
          <div className="wizard-stepper">
            {WIZARD_STEPS.map((label, idx) => (
              <button
                key={label}
                type="button"
                className={`wizard-step ${step === idx + 1 ? 'active' : ''} ${step > idx + 1 ? 'done' : ''}`}
                onClick={() => setStep(idx + 1)}
                disabled={saving}
              >
                <span>{idx + 1}</span>
                {label}
              </button>
            ))}
          </div>
          <div className="wizard-progress">
            <div className="wizard-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="edit-course-shell">
            {step === 1 && (
            <div className="edit-course-grid">
              <div className="edit-course-field full">
                <label>Course Title</label>
                <input
                  value={basicInfo.course_name}
                  onChange={(e) => {
                    markDirty();
                    setBasicInfo((p) => ({ ...p, course_name: e.target.value }));
                  }}
                />
              </div>
              <div className="edit-course-field full">
                <label>Description</label>
                <textarea
                  rows={5}
                  value={basicInfo.description}
                  onChange={(e) => {
                    markDirty();
                    setBasicInfo((p) => ({ ...p, description: e.target.value }));
                  }}
                />
              </div>
              <div className="edit-course-field">
                <label>Program</label>
                <select
                  value={basicInfo.prg_id}
                  onChange={(e) => {
                    markDirty();
                    setBasicInfo((p) => ({ ...p, prg_id: e.target.value }));
                  }}
                >
                  <option value="">Select Program</option>
                  {programs.map((p) => (
                    <option key={p.pgm_id} value={p.pgm_id}>{p.pgm_name}</option>
                  ))}
                </select>
              </div>
              <div className="edit-course-field">
                <label>Academic Year</label>
                <select
                  value={basicInfo.acd_id}
                  onChange={(e) => {
                    markDirty();
                    setBasicInfo((p) => ({ ...p, acd_id: e.target.value }));
                  }}
                >
                  <option value="">Select Academic Year</option>
                  {academicYears.map((y) => (
                    <option key={y.acd_id} value={y.acd_id}>{y.year_name || y.acd_id}</option>
                  ))}
                </select>
              </div>
              <div className="edit-course-field">
                <label>Total Weeks</label>
                <input
                  type="number"
                  min={1}
                  value={basicInfo.duration_weeks}
                  onChange={(e) => {
                    const val = Number(e.target.value || 1);
                    markDirty();
                    setBasicInfo((p) => ({ ...p, duration_weeks: val }));
                  }}
                />
              </div>
              <div className="edit-course-field">
                <label>Difficulty</label>
                <select
                  value={basicInfo.difficulty_level}
                  onChange={(e) => {
                    markDirty();
                    setBasicInfo((p) => ({ ...p, difficulty_level: e.target.value }));
                  }}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div className="edit-course-field">
                <label>Status</label>
                <select
                  value={basicInfo.status}
                  onChange={(e) => {
                    markDirty();
                    setBasicInfo((p) => ({ ...p, status: e.target.value }));
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="edit-course-field">
                <label>Thumbnail (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    markDirty();
                    setThumbnailFile(e.target.files?.[0] || null);
                  }}
                />
                <small>{thumbnailFile ? thumbnailFile.name : 'No new thumbnail selected'}</small>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="syllabus-builder">

              {/* ======== SETUP MODE ======== */}
              {!unitsGenerated && (
                <div className="syl-setup">
                  <div className="syl-setup-icon">
                    <svg width="56" height="56" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                    </svg>
                  </div>
                  <h3 className="syl-setup-title">How many units are in this course?</h3>
                  <p className="syl-setup-sub">Each unit represents a distinct topic or module in your syllabus.<br/>You can always adjust the count later.</p>

                  <div className="syl-setup-row">
                    <input
                      className="syl-setup-input"
                      type="number"
                      min="1"
                      max="30"
                      placeholder="e.g. 5"
                      value={unitCount}
                      onChange={(e) => setUnitCount(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') generateUnits(); }}
                      autoFocus
                    />
                    <button type="button" className="syl-setup-btn" onClick={generateUnits}>
                      Generate Units
                    </button>
                  </div>
                </div>
              )}

              {/* ======== DETAIL MODE ======== */}
              {unitsGenerated && (
                <>
                  {/* Header with Reset */}
                  <div className="syl-detail-header">
                    <div>
                      <h3 className="syl-heading">Course Syllabus</h3>
                      <p className="syl-subheading">{units.length} unit{units.length !== 1 ? 's' : ''} &mdash; fill in the title and topics for each unit. These will be mapped to weeks in Step&nbsp;3.</p>
                    </div>
                    <button type="button" className="syl-reset-btn" onClick={resetUnitCount}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 105.64-12.36L1 10"/></svg>
                      Reset Count
                    </button>
                  </div>

                  {/* Unit Cards */}
                  <div className="unit-card-stack">
                    {units.map((unit, idx) => (
                      <div className="unit-card" key={unit.unit_id || `unit_${idx}`}>
                        <div className="unit-card-header">
                          <span className="unit-card-badge">Unit {idx + 1}</span>
                          {unit.title && unit.title.trim() && <span className="unit-card-check">&#10003;</span>}
                        </div>
                        <div className="unit-card-fields">
                          <div className="unit-field">
                            <label className="unit-field-label">Unit Title <span className="req">*</span></label>
                            <input
                              className="unit-input"
                              type="text"
                              placeholder={`e.g., ${idx === 0 ? 'Introduction to AI' : idx === 1 ? 'Machine Learning Fundamentals' : `Unit ${idx + 1} title`}`}
                              value={unit.title}
                              onChange={(e) => updateUnit(idx, 'title', e.target.value)}
                            />
                          </div>
                          <div className="unit-field">
                            <label className="unit-field-label">Topics / Description</label>
                            <textarea
                              className="unit-textarea"
                              placeholder="List the key topics covered in this unit..."
                              rows={3}
                              value={unit.description}
                              onChange={(e) => updateUnit(idx, 'description', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Status Bar */}
                  <div className="syl-status">
                    {allUnitTitlesFilled
                      ? <span className="syl-status-ok">
                          All {units.length} unit title{units.length !== 1 ? 's' : ''} filled &mdash; click <strong>Next</strong> to save &amp; continue
                        </span>
                      : <span className="syl-status-warn">
                          {units.filter(u => !u.title || !u.title.trim()).length} of {units.length} unit title{units.filter(u => !u.title || !u.title.trim()).length !== 1 ? 's' : ''} still empty &mdash; all titles are required
                        </span>
                    }
                  </div>
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="wizard-inline-actions">
                <Button type="button" onClick={addWeek}>+ Add Week</Button>
              </div>
              <div className="week-list">
                {structureWeeks.map((w) => (
                  <div className="week-card" key={`wk_${w.week_no}`}>
                    <div className="week-card-head">
                      <strong>Week {w.week_no}</strong>
                      <Button type="button" variant="danger" onClick={() => removeWeek(w.week_no)}>Remove</Button>
                    </div>
                    <div className="edit-course-field">
                      <label>Week Title</label>
                      <input value={w.title} onChange={(e) => updateWeek(w.week_no, 'title', e.target.value)} />
                    </div>
                    <div className="edit-course-field">
                      <label>Week Description</label>
                      <textarea rows={3} value={w.description} onChange={(e) => updateWeek(w.week_no, 'description', e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="week-list">
              {structureWeeks.map((w) => (
                <div className="week-card" key={`final_wk_${w.week_no}`}>
                  <div className="week-card-head">
                    <strong>Week {w.week_no} Content</strong>
                    <Button type="button" onClick={() => addContent(w.week_no)}>+ Add Content</Button>
                  </div>
                  {(contents.filter((c) => !c.delete && Number(c.week_no) === Number(w.week_no))).map((c) => (
                    <div className="line-editor" key={c.local_id}>
                      <input placeholder="Title" value={c.title} onChange={(e) => upsertContent(c.local_id, 'title', e.target.value)} />
                      <select value={c.content_type} onChange={(e) => upsertContent(c.local_id, 'content_type', e.target.value)}>
                        <option value="note">Note</option>
                        <option value="video">Video</option>
                        <option value="document">Document</option>
                        <option value="assignment">Assignment</option>
                        <option value="test">Test</option>
                        <option value="others">Others</option>
                      </select>
                      <input type="file" onChange={(e) => upsertContent(c.local_id, 'file', e.target.files?.[0] || null)} />
                      <small>{c.file ? `New: ${c.file.name}` : (c.existing_file || 'No file')}</small>
                      <Button type="button" variant="danger" onClick={() => deleteContent(c.local_id)}>Delete</Button>
                    </div>
                  ))}

                  <div className="week-card-head">
                    <strong>Week {w.week_no} Assignments</strong>
                    <Button type="button" onClick={() => addAssignment(w.week_no)}>+ Add Assignment</Button>
                  </div>
                  {(assignments.filter((a) => !a.delete && Number(a.week_no) === Number(w.week_no))).map((a) => (
                    <div className="line-editor line-editor-block" key={a.local_id}>
                      <input placeholder="Assignment title" value={a.title} onChange={(e) => upsertAssignment(a.local_id, 'title', e.target.value)} />
                      <textarea placeholder="Description" rows={2} value={a.description} onChange={(e) => upsertAssignment(a.local_id, 'description', e.target.value)} />
                      <input type="date" value={a.due_date || ''} onChange={(e) => upsertAssignment(a.local_id, 'due_date', e.target.value)} />
                      <input type="file" onChange={(e) => upsertAssignment(a.local_id, 'file', e.target.files?.[0] || null)} />
                      <small>{a.file ? `New: ${a.file.name}` : (a.existing_file || 'No file')}</small>
                      <Button type="button" variant="danger" onClick={() => deleteAssignment(a.local_id)}>Delete</Button>
                    </div>
                  ))}

                  <div className="week-card-head">
                    <strong>Week {w.week_no} Tests</strong>
                    <Button type="button" onClick={() => addTest(w.week_no)}>+ Add Test</Button>
                  </div>
                  {(tests.filter((t) => !t.delete && Number(t.week_no) === Number(w.week_no))).map((t) => (
                    <div className="line-editor line-editor-block" key={t.local_id}>
                      <input placeholder="Test title" value={t.title} onChange={(e) => upsertTest(t.local_id, 'title', e.target.value)} />
                      <div className="test-meta">
                        <select value={t.type} onChange={(e) => upsertTest(t.local_id, 'type', e.target.value)}>
                          <option value="mcq">MCQ</option>
                          <option value="typing">Typing</option>
                        </select>
                        <input type="number" min={1} value={t.duration_minutes} onChange={(e) => upsertTest(t.local_id, 'duration_minutes', Number(e.target.value || 30))} />
                        <Button type="button" onClick={() => addQuestion(t.local_id)}>+ Question</Button>
                        <Button type="button" variant="danger" onClick={() => deleteTest(t.local_id)}>Delete Test</Button>
                      </div>
                      {(t.questions || []).map((q, qIdx) => (
                        <div className="question-editor" key={`${t.local_id}_${qIdx}`}>
                          <input placeholder="Question" value={q.question || ''} onChange={(e) => updateQuestion(t.local_id, qIdx, 'question', e.target.value)} />
                          {t.type === 'mcq' && (
                            <>
                              {[0, 1, 2, 3].map((oi) => (
                                <input
                                  key={oi}
                                  placeholder={`Option ${oi + 1}`}
                                  value={(q.options && q.options[oi]) || ''}
                                  onChange={(e) => {
                                    const options = [...(q.options || ['', '', '', ''])];
                                    options[oi] = e.target.value;
                                    updateQuestion(t.local_id, qIdx, 'options', options);
                                  }}
                                />
                              ))}
                              <input placeholder="Correct answer (option text/number)" value={q.answer || ''} onChange={(e) => updateQuestion(t.local_id, qIdx, 'answer', e.target.value)} />
                            </>
                          )}
                          <input type="number" min={1} placeholder="Marks" value={q.marks || 1} onChange={(e) => updateQuestion(t.local_id, qIdx, 'marks', Number(e.target.value || 1))} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="edit-course-actions">
          <Button type="button" variant="secondary" onClick={step === 1 ? cancelEdit : goBack} disabled={saving}>
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          {step < 4 ? (
            <Button type="button" onClick={goNext} disabled={saving || (step === 2 && !allUnitTitlesFilled)}>Next</Button>
          ) : (
            <Button type="button" onClick={handleFinalUpdate} disabled={saving}>
              {saving ? 'Updating...' : 'Update Course'}
            </Button>
          )}
        </div>
        </div>
      )}
      </div>
    </FacultyLayout>
  );
}
